import { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    TaskTypeStatus,
    type LeadType,
    type ObjectType,
    type UserType,
} from '@econtractor/types';
import { useAuth } from 'context/AuthContext';
import { canCreateTasks } from 'utils/helpers/access/hasUserAccess';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import TitleCounter from 'components/others/TitleCounter';
import { useTask } from '../TaskContext';
import TaskFilter from '../shared/TaskFilter';
import NewTask from '../NewTask';
import {
    clearBoardFilters,
    saveBoardFilters,
    toLeadSnapshot,
    toObjectSnapshot,
    toUserSnapshot,
} from './helpers/boardFiltersStorage';
import Kanban from './Kanban';
import SolarIcon from 'components/icons/SolarIcon';

export const BOARD_EXCLUDE_STATUSES = [TaskTypeStatus.CANCELLED];

export type BoardBodyProps = {
    initialExecutorUser: UserType | null;
    initialControllerUser: UserType | null;
    initialLead: LeadType | null;
    initialObject: ObjectType | null;
    canFilterByUsers: boolean;
};

const BoardBody = ({
    initialExecutorUser,
    initialControllerUser,
    initialLead,
    initialObject,
    canFilterByUsers,
}: BoardBodyProps) => {
    const { t } = useTranslation(['Tasks']);
    const { auth } = useAuth();
    const userId = Number(auth?.user?.ID || 0);
    const canCreate = canCreateTasks(auth?.user);
    const {
        triggerListRefresh,
        triggerListReset,
        paginator,
        tasksStatus,
        tasksType,
        tasksExecutor,
        tasksController,
        setTasksExecutor,
        setTasksController,
        filterLeadId,
        filterObjectId,
        onlyPersonal,
    } = useTask();
    const [showNewTask, setShowNewTask] = useState(false);
    const [executorUser, setExecutorUser] = useState<UserType | null>(
        canFilterByUsers ? initialExecutorUser : null,
    );
    const [controllerUser, setControllerUser] = useState<UserType | null>(
        canFilterByUsers ? initialControllerUser : null,
    );
    const [lead, setLead] = useState<LeadType | null>(initialLead);
    const [object, setObject] = useState<ObjectType | null>(initialObject);

    useEffect(() => {
        if (canFilterByUsers) return;
        setExecutorUser(null);
        setControllerUser(null);
        setTasksExecutor(null);
        setTasksController(null);
    }, [canFilterByUsers, setTasksExecutor, setTasksController]);

    useEffect(() => {
        saveBoardFilters(userId, {
            status: tasksStatus,
            type: tasksType,
            executor: canFilterByUsers ? tasksExecutor : null,
            controller: canFilterByUsers ? tasksController : null,
            executorUsers:
                canFilterByUsers && toUserSnapshot(executorUser)
                    ? [toUserSnapshot(executorUser)!]
                    : null,
            controllerUsers:
                canFilterByUsers && toUserSnapshot(controllerUser)
                    ? [toUserSnapshot(controllerUser)!]
                    : null,
            leadId: onlyPersonal ? null : filterLeadId,
            objectId: onlyPersonal ? null : filterObjectId,
            lead: onlyPersonal ? null : toLeadSnapshot(lead),
            object: onlyPersonal ? null : toObjectSnapshot(object),
            onlyPersonal,
        });
    }, [
        userId,
        tasksStatus,
        tasksType,
        tasksExecutor,
        tasksController,
        executorUser,
        controllerUser,
        canFilterByUsers,
        filterLeadId,
        filterObjectId,
        lead,
        object,
        onlyPersonal,
    ]);

    const handleReset = () => {
        clearBoardFilters(userId);
        setExecutorUser(null);
        setControllerUser(null);
        setLead(null);
        setObject(null);
        triggerListReset();
    };

    return (
        <>
            <Row className="task-board-page position-relative">
                <Col className="col-sticky" sm={12}>
                    <div className="w-100 d-sm-flex pt-1 justify-content-between align-items-start">
                        <div>
                            <h4 className="p-0 m-0 mb-0 me-2 text-break">{t('title')}</h4>
                            <TitleCounter count={paginator?.countRows ?? null} />
                        </div>

                        <div className="mt-2 mt-sm-0 mb-3" style={{ minWidth: 100 }}>
                            <div className="page-toolbar-actions">
                                {canCreate && (
                                <Button
                                    className="fs-14 ms-1"
                                    size="sm"
                                    variant="success"
                                    onClick={(e) => {
                                        e.currentTarget.blur();
                                        setShowNewTask(true);
                                    }}
                                >
                                    <SolarIcon name="plus" className="fs-14 p-0" />
                                    <span className="ms-1">{t('toolNewTask')}</span>
                                </Button>
                                )}

                                <ListToolBarActions
                                    onReset={handleReset}
                                    onRefresh={triggerListRefresh}
                                />

                                <TaskFilter
                                    excludeStatuses={BOARD_EXCLUDE_STATUSES}
                                    showExecutorFilter={canFilterByUsers}
                                    showControllerFilter={canFilterByUsers}
                                    initialExecutorUser={
                                        canFilterByUsers ? initialExecutorUser : null
                                    }
                                    initialControllerUser={
                                        canFilterByUsers ? initialControllerUser : null
                                    }
                                    initialLead={initialLead}
                                    initialObject={initialObject}
                                    onExecutorUserChange={setExecutorUser}
                                    onControllerUserChange={setControllerUser}
                                    onLeadChange={setLead}
                                    onObjectChange={setObject}
                                />
                            </div>
                        </div>
                    </div>
                </Col>

                <Col sm={12} className="task-board-page__kanban">
                    <Kanban />
                </Col>
            </Row>

            {canCreate && (
            <NewTask
                show={showNewTask}
                onHide={() => setShowNewTask(false)}
                onCreated={() => {
                    setShowNewTask(false);
                    triggerListRefresh();
                }}
            />
            )}
        </>
    );
};

export default BoardBody;
